/**
 * A conjecture's prediction as a contract the local runner can execute, and what
 * an executed receipt says about it (D283).
 *
 * Some kill tests are computations — a simulation of the mechanism, a search for
 * a counterexample, a calculation, a re-analysis of data. For those the pieces
 * already existed: Research Lab's executable contracts (`core/experiments.ts`),
 * run in the sandboxed local runner. This module is the join, both ways.
 *
 * - **Into a contract.** A model proposes the computation: its two programs, its
 *   two datasets, one metric, and where that metric lands under each of the two
 *   expectations — declared before anything runs. The two competing hypotheses
 *   are built HERE, from the conjecture's own registered words and never from a
 *   model's paraphrase of them: the conjecture's claim against what it departs
 *   from, under the prediction's own condition. Their ids carry the registration
 *   fingerprint, so every receipt names the wording it tested. The registered
 *   record rides along as a consumed `claim` input, frozen into every receipt; a
 *   transplant's mapping rides as a `MechanismTransfer`, which holds dispatch
 *   until it is complete. Every file is `model-proposed` and the evaluator is an
 *   `unreviewed-proposal`: a model wrote the instrument, and nothing here says
 *   anybody checked it.
 * - **Out of a receipt.** `assessExperiment` reports which declared ranges the
 *   evaluator's mean fell in, and the reading below is exactly that. It is
 *   derived on every read and stored nowhere, so there is no copy to drift from
 *   the receipt, and no field a file edit could strengthen.
 *
 * What it never does:
 *
 * - **It never writes a node's `outcome`.** A receipt is not a result somebody
 *   recorded: an outcome is the person's, in their own words (`Outcome.observed`),
 *   and "execution receipts never manufacture node outcomes" is the executable
 *   research contract's own rule (D85, D87). A reading from an evaluator a model
 *   wrote, for a conjecture a model proposed, is also a model marking its own
 *   homework. So a reading stays beside the results you record — out of the track
 *   record, out of calibration — and always says who wrote its evaluator.
 * - **It never spends, and it never stops anything.** Proposing is one press;
 *   executing is Research Lab's own press; no reading feeds a run, a cue, an
 *   anomaly count or a stop condition (D87, D92).
 * - **A range declared after a result is not a prediction.** The first execution
 *   against a conjecture on a metric fixes that metric's ranges; a later receipt
 *   under other ranges is shown with that fact beside it, the way a result under
 *   wording registered after it is counted apart in the track record (D282).
 *
 * Pure: no store, no clock, no model.
 */

import {
  EXPERIMENT_LIMITS,
  artifactRevision,
  assessExperiment,
  proposeExperiment,
  type ExecutableHypothesis,
  type ExperimentReceipt,
  type ExperimentSpec,
  type ExperimentWorkspaceState,
  type MechanismTransfer,
  type ResearchArtifact,
} from './experiments'
import type { ModelStamp, ReasonNode, Session } from './types'
import { predictionClauses, registrationLive } from './conjecture'
import { EXECUTED_READING_LABEL } from './vocabulary'
import { calendarDay } from './dates'

/* ── the proposal ─────────────────────────────────────────────────────── */

/** How the computation bears on the prediction. */
export type ExecutableMethod = 'simulation' | 'counterexample-search' | 'calculation' | 'reanalysis'

export const EXECUTABLE_METHODS: readonly ExecutableMethod[] = [
  'simulation',
  'counterexample-search',
  'calculation',
  'reanalysis',
]

export const EXECUTABLE_METHOD_LABEL: Record<ExecutableMethod, string> = {
  simulation: 'a simulation of the mechanism',
  'counterexample-search': 'a search for a counterexample',
  calculation: 'a calculation',
  reanalysis: 'a re-analysis of data',
}

export interface ExecutableRange {
  minimum: number
  maximum: number
}

/** What a model proposed, after the client has read it and found it whole. */
export interface ExecutableProposal {
  title: string
  method: ExecutableMethod
  /** What a result here can show about the prediction, and what it cannot. */
  reach: string
  metric: { name: string; unit: string; minimum: number; maximum: number }
  /** Where the evaluator's mean lands if the conjecture's prediction holds. */
  conjectureRange: ExecutableRange
  /** Where it lands if the expectation the conjecture departs from holds. */
  consensusRange: ExecutableRange
  /** What the computation assumes that the world might not. */
  assumptions: string[]
  otherExplanation: string
  intervention: { variable: string; change: string; controls: string[] }
  candidate: { language: 'javascript' | 'python'; code: string }
  evaluator: { language: 'javascript' | 'python'; code: string }
  /** JSON text the candidate receives. */
  publicData: string
  /** JSON text only the evaluator receives. */
  heldOutData: string
  seeds: number[]
  /** A transplant's mapping, relation by relation. Null for every other move. */
  transfer: null | {
    sourceMechanism: string
    /** The source field's structure, as the proposing model states it. */
    source: string
    mapping: MechanismTransfer['mapping']
    preserved: string[]
    changedConditions: string[]
    failureTest: string
  }
}

/**
 * The execution allowance a proposed contract carries. Fixed by the client, never
 * chosen by the model: at most `EXECUTABLE_MAX_SEEDS` seeds, two processes each,
 * so one execution press is bounded at 100 seconds of process time — inside the
 * five minutes a local execution press authorizes. A person can widen it in
 * Research Lab, where it becomes a different contract.
 */
export const EXECUTABLE_LIMITS: ExperimentSpec['limits'] = {
  wallMs: 10_000,
  cpuSeconds: 8,
  memoryMb: 128,
  outputBytes: 32_000,
}
export const EXECUTABLE_MAX_SEEDS = 5
/** Each dataset a model writes. An eighth of the platform's allowance: a dataset
 *  a model types out should be one a person can read. */
export const EXECUTABLE_DATA_BYTES = 64_000
/** The files one contract adds to the workspace: two programs, two datasets, the
 *  registered conjecture, the design note, and a transplant's source. */
export const EXECUTABLE_ARTIFACTS = 7

const bytes = (v: string) => new TextEncoder().encode(v).byteLength
const obj = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && !Array.isArray(v)
const text = (v: unknown, max: number): string | null =>
  typeof v === 'string' && v.trim().length > 0 && v.length <= max ? v.trim() : null
const finite = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v)
const texts = (v: unknown, min: number, max: number, each: number): string[] | null => {
  if (!Array.isArray(v) || v.length < min || v.length > max) return null
  const out = v.map((x) => text(x, each))
  return out.every((x): x is string => x !== null) ? out : null
}
/** The contract's own id rule (`experiments.ts`), for the one id a model names. */
const METRIC_NAME = /^[a-zA-Z0-9][a-zA-Z0-9_.:-]{0,63}$/

function range(v: unknown): ExecutableRange | null {
  if (!obj(v) || !finite(v.minimum) || !finite(v.maximum) || v.minimum > v.maximum) return null
  return { minimum: v.minimum, maximum: v.maximum }
}

function program(v: unknown): ExecutableProposal['candidate'] | null {
  if (!obj(v) || (v.language !== 'javascript' && v.language !== 'python')) return null
  if (typeof v.code !== 'string' || !v.code.trim()) return null
  if (bytes(v.code) > EXPERIMENT_LIMITS.programBytes) return null
  return { language: v.language, code: v.code }
}

function dataset(v: unknown): string | null {
  if (typeof v !== 'string' || !v.trim() || bytes(v) > EXECUTABLE_DATA_BYTES) return null
  try {
    JSON.parse(v)
    return v
  } catch {
    return null
  }
}

/** Two ranges share no value. The one property a test of a prediction needs. */
export const disjoint = (a: ExecutableRange, b: ExecutableRange): boolean =>
  a.maximum < b.minimum || b.maximum < a.minimum

export type ProposalReading =
  { proposal: ExecutableProposal; notes: string[] } | { refused: string }

/**
 * Read one proposal the way admission reads a conjecture: whole and well-formed,
 * or refused with the reason — nothing is repaired, because a repaired test is a
 * test nobody proposed.
 */
export function readExecutableProposal(raw: unknown, transplant: boolean): ProposalReading {
  const refuse = (refused: string): ProposalReading => ({ refused })
  if (!obj(raw)) return refuse('the proposal did not arrive whole')
  const title = text(raw.title, 160)
  if (!title) return refuse('it came without a title')
  const method = EXECUTABLE_METHODS.find((m) => m === raw.method)
  if (!method) return refuse('it named no method this contract knows')
  const reach = text(raw.reach, 1200)
  if (!reach) return refuse('it did not say what a result could and could not show')
  const m = raw.metric
  if (
    !obj(m) ||
    typeof m.name !== 'string' ||
    !METRIC_NAME.test(m.name) ||
    !text(m.unit, 100) ||
    !finite(m.minimum) ||
    !finite(m.maximum) ||
    m.minimum >= m.maximum
  )
    return refuse('its metric is not one number with a name, a unit and bounds')
  const metric = {
    name: m.name,
    unit: text(m.unit, 100)!,
    minimum: m.minimum,
    maximum: m.maximum,
  }
  const conjectureRange = range(raw.conjectureRange)
  const consensusRange = range(raw.consensusRange)
  if (!conjectureRange || !consensusRange) return refuse('it did not declare both ranges')
  const inside = (r: ExecutableRange) => r.minimum >= metric.minimum && r.maximum <= metric.maximum
  if (!inside(conjectureRange) || !inside(consensusRange))
    return refuse('a declared range reaches outside the bounds its own metric can take')
  if (!disjoint(conjectureRange, consensusRange))
    return refuse(
      'its two ranges overlap, so no value of the metric could tell the insight from what it departs from',
    )
  const assumptions = texts(raw.assumptions, 1, 6, 400)
  if (!assumptions) return refuse('it stated no assumptions, or too many')
  const otherExplanation = text(raw.otherExplanation, 1200)
  if (!otherExplanation) return refuse('it named no other explanation')
  const iv = raw.intervention
  const intervention = obj(iv)
    ? {
        variable: text(iv.variable, 200),
        change: text(iv.change, 600),
        controls: texts(iv.controls, 1, 8, 300),
      }
    : null
  if (!intervention?.variable || !intervention.change || !intervention.controls)
    return refuse('it did not say what the test changes and what it holds fixed')
  const candidate = program(raw.candidate)
  const evaluator = program(raw.evaluator)
  if (!candidate || !evaluator)
    return refuse(
      `a program is missing, in a language the runner does not run, or over ${EXPERIMENT_LIMITS.programBytes.toLocaleString('en-US')} bytes`,
    )
  const publicData = dataset(raw.publicData)
  const heldOutData = dataset(raw.heldOutData)
  if (!publicData || !heldOutData)
    return refuse(
      `a dataset is missing, is not JSON, or is over ${EXECUTABLE_DATA_BYTES.toLocaleString('en-US')} bytes`,
    )
  const seeds = raw.seeds
  if (
    !Array.isArray(seeds) ||
    seeds.length < 1 ||
    seeds.length > EXECUTABLE_MAX_SEEDS ||
    !seeds.every((s) => Number.isSafeInteger(s) && s >= 0 && s <= 2147483647) ||
    new Set(seeds).size !== seeds.length
  )
    return refuse(`its seeds are not one to ${EXECUTABLE_MAX_SEEDS} distinct whole numbers`)

  const notes: string[] = []
  let transfer: ExecutableProposal['transfer'] = null
  if (transplant) {
    const t = raw.transfer
    if (!obj(t))
      return refuse('a transplant’s test carries its mapping, and this one came without it')
    const mapping = Array.isArray(t.mapping) ? t.mapping : []
    const rows = mapping.map((r) =>
      obj(r)
        ? {
            source: text(r.source, 200),
            target: text(r.target, 200),
            sourceUnit: text(r.sourceUnit, 100),
            targetUnit: text(r.targetUnit, 100),
            transform:
              typeof r.transform === 'string' && r.transform.length <= 400
                ? r.transform.trim()
                : null,
          }
        : null,
    )
    if (
      rows.length < 1 ||
      rows.length > 16 ||
      !rows.every(
        (r) => r && r.source && r.target && r.sourceUnit && r.targetUnit && r.transform !== null,
      )
    )
      return refuse('its mapping is not a list of variables carried over, each with its unit')
    const full = rows as MechanismTransfer['mapping']
    if (full.some((r) => r.sourceUnit !== r.targetUnit && !r.transform))
      return refuse('a row of its mapping changes units without saying how')
    const sourceMechanism = text(t.sourceMechanism, 400)
    const source = text(t.source, 4000)
    const preserved = texts(t.preserved, 1, 8, 400)
    const changedConditions = texts(t.changedConditions, 1, 8, 400)
    const failureTest = text(t.failureTest, 1200)
    if (!sourceMechanism || !source || !preserved || !changedConditions || !failureTest)
      return refuse(
        'its mapping did not say what it carries, what it preserves, what changes, and how it would break',
      )
    transfer = { sourceMechanism, source, mapping: full, preserved, changedConditions, failureTest }
  } else if (raw.transfer !== null && raw.transfer !== undefined) {
    notes.push('a mapping came with an insight that is not a transplant, and was dropped')
  }

  return {
    proposal: {
      title,
      method,
      reach,
      metric,
      conjectureRange,
      consensusRange,
      assumptions,
      otherExplanation,
      intervention: {
        variable: intervention.variable,
        change: intervention.change,
        controls: intervention.controls,
      },
      candidate,
      evaluator,
      publicData,
      heldOutData,
      seeds: [...seeds] as number[],
      transfer,
    },
    notes,
  }
}

/* ── into a contract ──────────────────────────────────────────────────── */

/** The hypothesis ids a conjecture contract carries: the prefix, then the
 *  registration fingerprint of the wording it tests. */
export const CONJECTURE_HYPOTHESIS = 'conjecture-'
export const CONSENSUS_HYPOTHESIS = 'consensus-'

/** The contract's own id rule, applied to a fingerprint before it names anything. */
const HASH_ID = /^[a-zA-Z0-9]{1,64}$/

/** The contract's per-field limit, which a conjecture's own words must fit in. */
const FIELD_CHARS = 4000

/** The prediction as the question a contract asks. */
export function executableQuestion(n: Pick<ReasonNode, 'conjecture'>): string {
  const p = predictionClauses(n.conjecture!.prediction)
  return `If ${p.condition}, does ${p.observable} show ${p.expected} rather than ${p.consensusExpects}?`
}

/**
 * Why no executable test can be proposed on this node right now — or null.
 *
 * Every reason is decided from the record before a request is sent: a press that
 * could only have been refused afterwards would have spent money to learn it.
 */
export function executableRefusal(
  session: Pick<Session, 'research'>,
  n: ReasonNode,
): string | null {
  const c = n.conjecture
  if (n.kind !== 'conjecture' || !c)
    return 'An executable test is built from an insight’s prediction, and this node has none.'
  if (!HASH_ID.test(c.registered.contentHash))
    return 'This insight’s registration cannot name a contract, so no receipt could say which wording it tested.'
  // The ids name the registration and the hypotheses carry the words on screen,
  // so the two have to be the same wording or a receipt would name one and test
  // the other.
  if (!registrationLive(n))
    return 'The wording on screen is not the wording that was registered, so a contract built now would name one and test the other.'
  const p = c.prediction
  const q = predictionClauses(p)
  const fields = [
    n.claim,
    c.departsFrom,
    p.condition,
    `${q.observable} shows ${q.expected}`,
    `${q.observable} shows ${q.consensusExpects}`,
  ]
  if (fields.some((f) => !f.trim() || f.length > FIELD_CHARS))
    return `Its wording does not fit an executable contract, which holds ${FIELD_CHARS.toLocaleString('en-US')} characters a field.`
  const w = session.research?.experiments
  if (
    w &&
    (w.experiments.length >= EXPERIMENT_LIMITS.experiments ||
      w.artifacts.length + EXECUTABLE_ARTIFACTS > EXPERIMENT_LIMITS.artifacts ||
      w.transfers.length >= 64)
  )
    return 'The Research Lab workspace is full. Start an evidence companion there before proposing another test.'
  return null
}

/**
 * The two competing hypotheses, in the conjecture's own registered words: its
 * claim against what it departs from, under its prediction's condition. The
 * model supplies the ranges and the assumptions its computation adds, and
 * nothing else in either.
 */
export function conjectureHypotheses(
  n: ReasonNode,
  proposal: Pick<
    ExecutableProposal,
    'metric' | 'conjectureRange' | 'consensusRange' | 'assumptions'
  >,
): ExecutableHypothesis[] {
  const c = n.conjecture!
  const p = c.prediction
  // Joined into one statement, the parts lose a closing full stop of their own
  // (`asClause`). The condition stands alone in its list, so it keeps its words
  // exactly, as `executableRefusal` measures them.
  const q = predictionClauses(p)
  const conditions = [p.condition, ...proposal.assumptions]
  const metric = proposal.metric.name
  return [
    {
      id: `${CONJECTURE_HYPOTHESIS}${c.registered.contentHash}`,
      mechanism: n.claim,
      conditions,
      prediction: { metric, ...proposal.conjectureRange },
      counterEvidence: `${q.observable} shows ${q.consensusExpects}`,
    },
    {
      id: `${CONSENSUS_HYPOTHESIS}${c.registered.contentHash}`,
      mechanism: c.departsFrom,
      conditions,
      prediction: { metric, ...proposal.consensusRange },
      counterEvidence: `${q.observable} shows ${q.expected}`,
    },
  ]
}

/** The files of one contract, named from its id. */
export const contractFiles = (id: string) => ({
  candidate: `${id}-candidate`,
  evaluator: `${id}-evaluator`,
  data: `${id}-data`,
  heldOut: `${id}-heldout`,
  conjecture: `${id}-conjecture`,
  design: `${id}-design`,
  source: `${id}-source`,
  transfer: `${id}-transfer`,
})

/** The design note a contract carries: how the computation bears on the
 *  prediction, in the proposing model's words. */
interface DesignNote {
  method: ExecutableMethod
  reach: string
  model: string
}

/**
 * One proposal as a contract: the spec, the files it consumes, and a
 * transplant's transfer. Unbound (`environment: null`) — the execution host binds
 * it, as every proposal in Research Lab is bound.
 */
export function conjectureContract(input: {
  id: string
  sessionId: string
  node: ReasonNode
  proposal: ExecutableProposal
  model: ModelStamp
}): { spec: ExperimentSpec; artifacts: ResearchArtifact[]; transfer: MechanismTransfer | null } {
  const { id, node, proposal } = input
  const c = node.conjecture!
  const files = contractFiles(id)
  const scope = `Written by ${input.model.name} as an executable test of one insight’s registered prediction (wording ${c.registered.contentHash}). A model proposed it, and nobody has reviewed it.`
  const artifact = (
    artifactId: string,
    kind: ResearchArtifact['kind'],
    content: string,
  ): ResearchArtifact => ({
    id: artifactId,
    kind,
    content,
    scope,
    provenance: 'model-proposed',
    dependsOn: [],
    freshness: { kind: 'immutable' },
  })
  const design: DesignNote = {
    method: proposal.method,
    reach: proposal.reach,
    model: input.model.name,
  }
  const artifacts = [
    artifact(files.candidate, 'code', proposal.candidate.code),
    artifact(files.evaluator, 'evaluator', proposal.evaluator.code),
    artifact(files.data, 'dataset', proposal.publicData),
    artifact(files.heldOut, 'dataset', proposal.heldOutData),
    // The wording under test, whole, so a receipt carries what it tested and not
    // only a fingerprint of it.
    artifact(
      files.conjecture,
      'claim',
      JSON.stringify({
        claim: node.claim,
        move: c.move,
        departsFrom: c.departsFrom,
        leap: c.leap,
        prediction: c.prediction,
        killTest: c.killTest,
        registered: c.registered,
      }),
    ),
    artifact(files.design, 'method', JSON.stringify(design)),
    ...(proposal.transfer ? [artifact(files.source, 'method', proposal.transfer.source)] : []),
  ]
  const spec: ExperimentSpec = {
    version: 1,
    id,
    title: proposal.title,
    question: executableQuestion(node),
    context: { sessionId: input.sessionId, nodeId: node.id, claim: node.claim, testId: null },
    hypotheses: conjectureHypotheses(node, proposal),
    otherExplanation: proposal.otherExplanation,
    intervention: proposal.intervention,
    candidate: { language: proposal.candidate.language, artifactId: files.candidate },
    evaluator: {
      language: proposal.evaluator.language,
      artifactId: files.evaluator,
      authority: 'unreviewed-proposal',
    },
    dataId: files.data,
    evaluatorDataId: files.heldOut,
    environment: null,
    inputs: artifacts.map((a) => ({ id: a.id, revision: artifactRevision(a) })),
    seeds: proposal.seeds,
    metric: {
      name: proposal.metric.name,
      unit: proposal.metric.unit,
      aggregation: 'mean',
      minimum: proposal.metric.minimum,
      maximum: proposal.metric.maximum,
    },
    limits: { ...EXECUTABLE_LIMITS },
  }
  const t = proposal.transfer
  const transfer: MechanismTransfer | null = t
    ? {
        id: files.transfer,
        sourceArtifactId: files.source,
        targetExperimentId: id,
        sourceMechanism: t.sourceMechanism,
        mapping: t.mapping,
        preserved: t.preserved,
        changedConditions: t.changedConditions,
        failureTest: t.failureTest,
      }
    : null
  return { spec, artifacts, transfer }
}

/**
 * The contract added to a workspace, transfer and all. Throws when the result
 * would not be a valid workspace — the caller refuses rather than repairs.
 */
export function withConjectureContract(
  w: ExperimentWorkspaceState,
  contract: ReturnType<typeof conjectureContract>,
): ExperimentWorkspaceState {
  const next = proposeExperiment(w, contract.spec, contract.artifacts)
  return contract.transfer
    ? {
        ...next,
        transfers: [
          ...next.transfers.filter((t) => t.id !== contract.transfer!.id),
          contract.transfer,
        ],
      }
    : next
}

/* ── out of a receipt ─────────────────────────────────────────────────── */

/** Which wording a contract tested, read off its hypothesis ids. */
interface Pair {
  conjecture: ExecutableHypothesis
  consensus: ExecutableHypothesis
  wording: 'current' | 'earlier'
}

/**
 * Whether a contract belongs to THIS document: proposed under this exploration's
 * id, or proposed by a pass on this node that this document's own ledger holds.
 *
 * A copy — a link opened, a branch, a copy, a file opened (D117, D173, D175) — is
 * a new document under a new id, and a contract keeps the id it was proposed
 * under. It has to: that id is part of every receipt's digested input, so
 * rebinding it on the way across would fail the receipt's integrity check and
 * claim the contract ran for an exploration that did not exist when it ran, and
 * no Research Lab import rebinds a context either. What a copy does carry is the
 * op ledger, and a conjecture contract's id IS the id of the pass that proposed
 * it (`conjectureContract`), so the ledger holding that pass, aimed at this node,
 * is the document vouching for the contract (D290).
 *
 * Two explorations that share a node id — because one is a copy of the other, or
 * both are copies of a third — share only the passes run before they parted. A
 * contract proposed on either side afterwards names a pass the other never ran,
 * and is refused there, whichever exploration's id it carries.
 */
function inThisDocument(
  session: Pick<Session, 'id' | 'ops'>,
  n: ReasonNode,
  spec: ExperimentSpec,
): boolean {
  if (spec.context.sessionId === session.id) return true
  return session.ops.some(
    (op) => op.id === spec.id && op.kind === 'research' && op.targetIds.includes(n.id),
  )
}

/**
 * The conjecture/consensus pair a spec carries for THIS conjecture, or null when
 * it is not a test of it: another document's, another node's, or a contract
 * whose ids name no registration this conjecture ever had.
 */
function pairOf(
  session: Pick<Session, 'id' | 'ops'>,
  n: ReasonNode,
  spec: ExperimentSpec,
): Pair | null {
  const c = n.conjecture
  if (!c || spec.context.nodeId !== n.id || !inThisDocument(session, n, spec)) return null
  const conj = spec.hypotheses.filter((h) => h.id.startsWith(CONJECTURE_HYPOTHESIS))
  const cons = spec.hypotheses.filter((h) => h.id.startsWith(CONSENSUS_HYPOTHESIS))
  if (conj.length !== 1 || cons.length !== 1) return null
  const hash = conj[0]!.id.slice(CONJECTURE_HYPOTHESIS.length)
  if (cons[0]!.id !== `${CONSENSUS_HYPOTHESIS}${hash}`) return null
  const wording =
    hash === c.registered.contentHash
      ? 'current'
      : (c.registrations ?? []).some((r) => r.contentHash === hash)
        ? 'earlier'
        : null
  if (!wording) return null
  return { conjecture: conj[0]!, consensus: cons[0]!, wording }
}

/** Whether a spec is a conjecture contract at all, whatever it tests — for a
 *  surface that has the spec and not the node. */
export function isConjectureContract(spec: ExperimentSpec): boolean {
  const conj = spec.hypotheses.filter((h) => h.id.startsWith(CONJECTURE_HYPOTHESIS))
  const cons = spec.hypotheses.filter((h) => h.id.startsWith(CONSENSUS_HYPOTHESIS))
  return (
    conj.length === 1 &&
    cons.length === 1 &&
    cons[0]!.id === `${CONSENSUS_HYPOTHESIS}${conj[0]!.id.slice(CONJECTURE_HYPOTHESIS.length)}`
  )
}

/** A contract's design note, read back from the files it consumed. Null when the
 *  file is missing or is not the note this module writes. */
export function designOf(
  spec: Pick<ExperimentSpec, 'id'>,
  artifacts: readonly ResearchArtifact[],
): { method: ExecutableMethod; reach: string; model: string } | null {
  const file = artifacts.find((a) => a.id === contractFiles(spec.id).design)
  if (!file) return null
  try {
    const v: unknown = JSON.parse(file.content)
    if (!obj(v)) return null
    const method = EXECUTABLE_METHODS.find((m) => m === v.method)
    if (!method || typeof v.reach !== 'string' || typeof v.model !== 'string') return null
    return { method, reach: v.reach, model: v.model }
  } catch {
    return null
  }
}

/** What one executed receipt says about the prediction. */
export type ExecutedReading = 'predicted' | 'against' | 'unsettled' | 'no-reading'

/**
 * The reading in words. The track record's vocabulary, for the same reason:
 * one execution is one execution, and a prediction a computation bore out once
 * has been borne out once — never "confirmed". Each is a status word, so the
 * table is defined in core/vocabulary.ts, built from the track record's own
 * clauses, and re-exported here (D287).
 */
export { EXECUTED_READING_LABEL }

/** Who wrote the instrument, said every time a reading is printed. */
export const EVALUATOR_AUTHORITY_PHRASE: Record<ExperimentSpec['evaluator']['authority'], string> =
  {
    'unreviewed-proposal': 'by an evaluator a model proposed and nobody has reviewed',
    'task-author':
      'by an evaluator declared to be its task author’s — a declaration nothing here authenticates',
  }

/** What a reading is not, said once wherever readings are listed. */
export const EXECUTED_NOT_A_RESULT =
  'An executed reading is not a result you recorded. It stays beside your results, out of the track record and out of calibration; if it settles the question for you, record the result in your own words.'

export interface ExecutedResult {
  receiptId: string
  experimentId: string
  title: string
  startedAt: number
  completedAt: number
  reading: ExecutedReading
  /** Why, in the assessment's own words or the join's. */
  why: string
  /** The evaluator's mean over the seeds, or null when there was no reading. */
  value: number | null
  metric: { name: string; unit: string }
  conjectureRange: ExecutableRange
  consensusRange: ExecutableRange
  authority: ExperimentSpec['evaluator']['authority']
  /** The registration its hypotheses name. */
  wording: 'current' | 'earlier'
  /**
   * Its ranges differ from those of the first execution against this conjecture
   * on the same metric: they were declared after a result had come back.
   */
  rangesMoved: boolean
}

const sameRange = (a: ExecutableRange, b: ExecutableRange) =>
  a.minimum === b.minimum && a.maximum === b.maximum

function readReceipt(receipt: ExperimentReceipt, pair: Pair): Omit<ExecutedResult, 'rangesMoved'> {
  const a = assessExperiment(receipt)
  const base = {
    receiptId: receipt.id,
    experimentId: receipt.experimentId,
    title: receipt.spec.title,
    startedAt: receipt.startedAt,
    completedAt: receipt.completedAt,
    metric: { name: receipt.spec.metric.name, unit: receipt.spec.metric.unit },
    conjectureRange: {
      minimum: pair.conjecture.prediction.minimum,
      maximum: pair.conjecture.prediction.maximum,
    },
    consensusRange: {
      minimum: pair.consensus.prediction.minimum,
      maximum: pair.consensus.prediction.maximum,
    },
    authority: receipt.spec.evaluator.authority,
    wording: pair.wording,
  }
  if (a.status === 'unassessed')
    return { ...base, reading: 'no-reading', why: a.reason, value: null }
  const held = a.compatibleHypothesisIds.includes(pair.conjecture.id)
  const departed = a.compatibleHypothesisIds.includes(pair.consensus.id)
  if (held && !departed) return { ...base, reading: 'predicted', why: a.reason, value: a.value }
  if (departed && !held) return { ...base, reading: 'against', why: a.reason, value: a.value }
  return {
    ...base,
    reading: 'unsettled',
    why: held
      ? 'Its two declared ranges overlap, so this value cannot tell them apart.'
      : 'The mean landed in neither declared range, so the contract’s other explanation stays open.',
    value: a.value,
  }
}

/**
 * Every executed receipt that tested this conjecture, newest first, each read by
 * `assessExperiment` and nothing else.
 */
export function executedResults(
  session: Pick<Session, 'id' | 'research' | 'ops'>,
  n: ReasonNode,
): ExecutedResult[] {
  const receipts = session.research?.experiments?.receipts ?? []
  const rows: Omit<ExecutedResult, 'rangesMoved'>[] = []
  for (const r of receipts) {
    const pair = pairOf(session, n, r.spec)
    if (pair) rows.push(readReceipt(r, pair))
  }
  // The first execution on each metric fixes that metric's ranges.
  const byTime = [...rows].sort(
    (a, b) => a.completedAt - b.completedAt || a.receiptId.localeCompare(b.receiptId),
  )
  const out = rows.map((row) => {
    const first = byTime.find((x) => x.metric.name === row.metric.name)!
    const rangesMoved =
      first.receiptId !== row.receiptId &&
      row.startedAt >= first.completedAt &&
      !(
        sameRange(first.conjectureRange, row.conjectureRange) &&
        sameRange(first.consensusRange, row.consensusRange)
      )
    return { ...row, rangesMoved }
  })
  return out.sort((a, b) => b.completedAt - a.completedAt || b.receiptId.localeCompare(a.receiptId))
}

/** One proposed contract testing this conjecture, with what its receipts read. */
export interface ConjectureContract {
  spec: ExperimentSpec
  wording: 'current' | 'earlier'
  design: ReturnType<typeof designOf>
  /** The two ranges the contract declares now, before or after any run. */
  conjectureRange: ExecutableRange
  consensusRange: ExecutableRange
  results: ExecutedResult[]
}

/** The contracts in the workspace that test this conjecture, in workspace order. */
export function conjectureContracts(
  session: Pick<Session, 'id' | 'research' | 'ops'>,
  n: ReasonNode,
): ConjectureContract[] {
  const w = session.research?.experiments
  if (!w) return []
  const results = executedResults(session, n)
  const out: ConjectureContract[] = []
  for (const spec of w.experiments) {
    const pair = pairOf(session, n, spec)
    if (!pair) continue
    const { minimum: cMin, maximum: cMax } = pair.conjecture.prediction
    const { minimum: xMin, maximum: xMax } = pair.consensus.prediction
    out.push({
      spec,
      wording: pair.wording,
      design: designOf(spec, w.artifacts),
      conjectureRange: { minimum: cMin, maximum: cMax },
      consensusRange: { minimum: xMin, maximum: xMax },
      results: results.filter((r) => r.experimentId === spec.id),
    })
  }
  return out
}

/** A number as a reader wants it: four significant figures, no float noise. */
export const fmtMetric = (x: number): string =>
  Number.isInteger(x) ? String(x) : String(Number(x.toPrecision(4)))

const fmtRange = (r: ExecutableRange) => `[${fmtMetric(r.minimum)}, ${fmtMetric(r.maximum)}]`

/** The declared ranges, as every surface prints them. */
export function rangesSentence(
  r: Pick<ExecutedResult, 'conjectureRange' | 'consensusRange'>,
): string {
  return `the prediction declared ${fmtRange(r.conjectureRange)}, the expectation it departs from ${fmtRange(r.consensusRange)}`
}

/**
 * One executed reading as the one sentence every surface prints: when, what it
 * read, the mean and both declared ranges, who wrote the evaluator, and what
 * qualifies it.
 */
export function executedSentence(r: ExecutedResult): string {
  // The reader's day, as every other record's (D210): the UTC one dated a run
  // made before breakfast in Tokyo the day before.
  const day = calendarDay(r.completedAt)
  const what =
    r.value === null
      ? `${EXECUTED_READING_LABEL[r.reading]}: ${r.why}`
      : `${EXECUTED_READING_LABEL[r.reading]} — mean ${r.metric.name} ${fmtMetric(r.value)} ${r.metric.unit}; ${rangesSentence(r)}.${r.reading === 'unsettled' ? ` ${r.why}` : ''}`
  const qualifiers = [
    r.wording === 'earlier' ? 'It tested an earlier wording of this insight.' : '',
    r.rangesMoved
      ? 'Its ranges differ from those of the first execution on this metric, so they were declared after a result had come back — and a range set after a result is not a prediction.'
      : '',
  ].filter(Boolean)
  return `Executed ${day}: ${what.replace(/\.?$/, '.')} Judged ${EVALUATOR_AUTHORITY_PHRASE[r.authority]}.${qualifiers.length ? ` ${qualifiers.join(' ')}` : ''}`
}
